import { useCallback, useRef } from 'react'
import { Map as MapLibre } from 'react-map-gl/maplibre'
import type { ErrorEvent, MapRef, ViewStateChangeEvent } from 'react-map-gl/maplibre'
import maplibregl from 'maplibre-gl'
import { captureException, track } from '~/analytics'
import { useAppStore, usePMTiles, useMapHover, useMapClick, useMapLibreFrameRate } from '~/hooks'
import { BASEMAP_STYLES, DIRECTIONAL_ATTRIBUTES } from './constants'

import { DeckGLOverlay } from '~/components/DeckGLOverlay'
import FloodLayer from './layers/FloodLayer'
import ContourFloodLayer from './layers/ContourFloodLayer'
import { MapTooltip } from './components/MapTooltip'
import { MapTooltipMachineContext } from './contexts/MapTooltipMachineContext'
import { BuildingLayer } from './layers/BuildingLayer'
import { RoadLayer } from './layers/RoadLayer'
import { AdaptationLayer } from './layers/AdaptationLayer'
import { BathymetryLayer } from './layers/BathymetryLayer'
import { ErosionLayer } from './layers/ErosionLayer'
import WavePointsLayer from './layers/WavePointsLayer'
import WavePointTextLayer from './layers/WavePointTextLayer'

// Import cogConfig to ensure color functions are registered
import './util/cogConfig'

// A missing tile or COG fails once per tile in view; report each distinct
// message once per session so one bad layer doesn't flood PostHog.
const reportedMapErrors = new Set<string>()

function onMapError({ error }: ErrorEvent) {
  console.error(error)
  if (reportedMapErrors.has(error.message)) return
  reportedMapErrors.add(error.message)
  captureException(error, { source: 'maplibre', url: (error as { url?: string }).url })
}

export default function Map() {
  const mapRef = useRef<MapRef | null>(null)
  const {
    viewState,
    dataset,
    setViewState,
    setMapRef,
    basemapType,
    getCosmosScenario,
    wavesProperty,
  } = useAppStore()
  const { layerInfo } = usePMTiles()

  const deckOverlayActive = dataset === 'waves' && DIRECTIONAL_ATTRIBUTES.includes(wavesProperty)

  const { onMouseMove } = useMapHover()
  const { onClick } = useMapClick()
  const tooltipActor = MapTooltipMachineContext.useActorRef()
  const { send } = tooltipActor
  const { coordinates } = MapTooltipMachineContext.useSelector((s) => s.context)
  const scenario = getCosmosScenario()
  const handleFrameRateChange = useCallback((fps: number) => {
    track('frame_rate', { fps })
  }, [])
  const {
    onRender: frameRateOnRender,
    enable: enableFrameRate,
    disable: disableFrameRate,
  } = useMapLibreFrameRate({
    onSignificantChange: handleFrameRateChange,
  })

  const onMapLoad = () => {
    if (mapRef.current) {
      setMapRef(mapRef.current)

      // When a single user is using the map, we calculate the screen X/Y from
      // the coordinates of the hovered/clicked wave point when the initial
      // click/hover event happens, and we update it if the user moves the map.
      // However, when one user shares a link with another user, we know the
      // coordinates and name of the wave point, but we don't know its screen
      // X/Y (the only case this _wouldn't_ be different is if the two users
      // are on _exactly_ the same size screen). To handle that case, once the
      // map has loaded we send the tooltip machine a message saying: "Hey, the
      // coordinates you currently have loaded correspond to this screen
      // position". This allows the tooltip to update its position correctly.
      if (coordinates) {
        const projected = mapRef.current.project([coordinates.lng, coordinates.lat])
        send({
          type: 'UPDATE_POSITION',
          x: projected.x,
          y: projected.y,
        })
      }
    }
  }

  return (
    <div>
      <MapTooltip />
      <MapLibre
        key={basemapType}
        onMove={(evt: ViewStateChangeEvent) => {
          setViewState(evt.viewState)
          const snapshot = tooltipActor.getSnapshot()
          const tooltipCoordinates = snapshot.context.coordinates
          const map = mapRef.current?.getMap()

          if (!tooltipCoordinates || !map) {
            return
          }

          if (snapshot.value === 'visible_from_click') {
            const projected = map.project([tooltipCoordinates.lng, tooltipCoordinates.lat])
            send({
              type: 'UPDATE_POSITION',
              x: projected.x,
              y: projected.y,
            })
          } else if (snapshot.value === 'visible_from_hover') {
            send({ type: 'MAP_MOVED' })
          }
        }}
        initialViewState={viewState}
        mapLib={maplibregl}
        maxZoom={26}
        style={{ width: '100vw', height: '100vh' }}
        onLoad={onMapLoad}
        onError={onMapError}
        mapStyle={BASEMAP_STYLES[basemapType]}
        ref={mapRef}
        onMouseMove={onMouseMove}
        onMoveStart={enableFrameRate}
        onMoveEnd={disableFrameRate}
        onRender={frameRateOnRender}
        onMouseLeave={() => {
          // Reset cursor when mouse leaves the map
          if (mapRef.current?.getMap()) {
            const canvas = mapRef.current.getMap().getCanvasContainer()
            if (canvas) {
              canvas.style.cursor = ''
            }
          }
          send({ type: 'MOUSE_LEAVE' })
        }}
        onClick={onClick}
      >
        {layerInfo && scenario && (
          <>
            <BathymetryLayer />
            {/* Building must mount before layers that use beforeId={LayerId.BUILDING}. */}
            <BuildingLayer />
            <ErosionLayer />
            <FloodLayer />
            <ContourFloodLayer />
            <RoadLayer />
            <AdaptationLayer />
            <WavePointsLayer />
            <WavePointTextLayer />
            {deckOverlayActive && <DeckGLOverlay />}
          </>
        )}
      </MapLibre>
    </div>
  )
}
